import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  TableRow,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link
} from '@mui/material';
import { Search, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Link as LinkIcon, CloudUpload, Print, Visibility, TextSnippet, ReceiptLong, ViewColumn, ArrowBack } from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { purchaseOrderService } from '../services/purchaseOrderService';
import { vendorService } from '../services/vendorService';
import { employeeService } from '../services/employeeService';
import { vendorInvoiceService } from '../services/vendorInvoiceService';
import { attachmentService } from '../services/attachmentService';
import PrintPage from '../components/print/PrintPage';
import { printWithFilename } from '../utils/printUtils';
import '../styles/print.css';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { formatDateOnly, formatLocalTodayYmd, sortTimestampFromDateOnly } from '../utils/dateUtils';
import {
  ProcurementSummaryBar,
  ProcurementSummaryField,
  PoReceiptStatusChip,
  getPoReceiptStatusChipProps,
  ShipmentsReceivedSummaryLinks
} from '../components/procurement/ProcurementSummaryBar';
import { formatCurrency } from '../components/CurrencyTextField';
import { computePOLineExtended, computePOLinesSubtotal } from '../utils/purchaseOrderLines';

const emptyPOForm = () => ({
  po_number: '',
  vendor_id: '',
  vendor_name: '',
  ordered_by_id: '',
  order_date: formatLocalTodayYmd(),
  expected_date: '',
  ship_via: '',
  notes: ''
});

const emptyLineItem = () => ({
  line_number: 1,
  part_id: null,
  part_number: '',
  description: '',
  quantity_ordered: 0,
  unit: 'EA',
  unit_price: '',
  notes: '',
  work_order_id: null,
  work_order_item_id: null
});

const SHIP_VIA_OPTIONS = ['UPS', 'USPS', 'DHL', 'FedEx', 'In-house carrier'];

const getPoShipmentCount = (row) => {
  const n = Number(row?.shipments_in_count);
  if (Number.isFinite(n)) return n;
  return row?.has_receiver ? 1 : 0;
};

const validLineItems = (items) =>
  items.filter((it) => (it.part_number || it.description || '').trim() || Number(it.quantity_ordered) > 0);

const ProcurementPO = () => {
  const DEFAULT_PO_COL_WIDTHS = {
    po: 170,
    vendor: 220,
    orderedBy: 180,
    orderDate: 120,
    expected: 120,
    shipmentsReceived: 160,
    shipVia: 150,
    status: 120,
    actions: 120
  };
  const navigate = useNavigate();
  const { id: poIdParam } = useParams();
  const isDetailMode = Boolean(poIdParam);
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newPoIdentifierPreview, setNewPoIdentifierPreview] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [poForm, setPoForm] = useState(emptyPOForm);
  const [lineItems, setLineItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [linkedReceivers, setLinkedReceivers] = useState([]);
  const [linkedInvoices, setLinkedInvoices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState('');
  const [isDraggingOverAttachment, setIsDraggingOverAttachment] = useState(false);
  const {
    colWidths,
    setColWidths,
    startResize,
    saveColWidths,
    resetColWidths
  } = useResizableColumns({
    defaultWidths: DEFAULT_PO_COL_WIDTHS,
    storageKey: 'wm_procurement_po_col_widths',
    minWidths: {
      po: 120,
      vendor: 160,
      orderedBy: 140,
      orderDate: 100,
      expected: 100,
      shipmentsReceived: 110,
      shipVia: 110,
      status: 100,
      actions: 100
    }
  });
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [sortKey, setSortKey] = useState('po_number');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (key) => {
    setSortDirection((prev) => (sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  };

  const displayedPos = useMemo(() => {
    const list = [...pos];
    const getter = (row) => {
      switch (sortKey) {
        case 'po_number': return row.identifier ?? row.po_number ?? '';
        case 'vendor': return row.vendor_name ?? '';
        case 'ordered_by': return `${row.ordered_by_first_name || ''} ${row.ordered_by_last_name || ''}`.trim();
        case 'order_date': return sortTimestampFromDateOnly(row.order_date);
        case 'expected_date': return sortTimestampFromDateOnly(row.expected_date);
        case 'shipments_in_count': {
          const n = Number(row.shipments_in_count);
          return Number.isFinite(n) ? n : row.has_receiver ? 1 : 0;
        }
        case 'ship_via': return row.ship_via ?? '';
        case 'status': return row.status ?? '';
        default: return '';
      }
    };
    const dir = sortDirection === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      if (typeof va === 'number' && typeof vb === 'number') return dir * (va - vb);
      return dir * String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    });
    return list;
  }, [pos, sortKey, sortDirection]);

  useEffect(() => {
    if (!newDialogOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await purchaseOrderService.getNextIdentifierPreview({
          order_date: poForm.order_date
        });
        const prev = res?.data?.identifier_preview ?? '';
        if (!cancelled) setNewPoIdentifierPreview(prev);
      } catch {
        if (!cancelled) setNewPoIdentifierPreview('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [newDialogOpen, poForm.order_date]);

  const loadPOs = async () => {
    try {
      setLoading(true);
      setError('');
      if (isDetailMode && poIdParam) {
        const poRes = await purchaseOrderService.getById(poIdParam);
        const poData = poRes?.data;
        if (!poData) {
          setPos([]);
          setSelectedPO(null);
          return;
        }
        setPos([poData]);
        setSelectedPO(poData);
        setPoForm({
          po_number: poData.identifier ?? poData.po_number ?? '',
          vendor_id: poData.vendor_id ?? '',
          vendor_name: poData.vendor_name ?? '',
          ordered_by_id: poData.ordered_by_id ?? '',
          order_date: (poData.order_date || '').toString().slice(0, 10) || formatLocalTodayYmd(),
          expected_date: (poData.expected_date || '').toString().slice(0, 10) || '',
          ship_via: poData.ship_via ?? '',
          notes: poData.notes ?? ''
        });
        setLineItems(
          (poData.items || []).length > 0
            ? poData.items.map((it) => ({
                id: it.id,
                line_number: it.line_number ?? 0,
                part_number: it.part_number ?? '',
                description: it.description ?? it.part_description ?? '',
                quantity_ordered: parseInt(it.quantity_ordered, 10) || 0,
                unit: it.unit ?? 'EA',
                unit_price: it.unit_price ?? '',
                notes: it.notes ?? '',
                work_order_id: it.work_order_id ?? null,
                work_order_item_id: it.work_order_item_id ?? null
              }))
            : []
        );
        return;
      }
      const res = await purchaseOrderService.getAll({
        search: searchTerm || undefined,
        limit: 100
      });
      const list = res?.data ?? [];
      setPos(list);
      // Keep current selection only if it still exists in result set.
      setSelectedPO((prev) => {
        if (!prev?.id) return null;
        return list.find((row) => String(row.id) === String(prev.id)) || null;
      });
    } catch (err) {
      console.error('Error loading purchase orders:', err);
      setError(err.response?.data?.message || 'Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPOs();
  }, [poIdParam]);

  useEffect(() => {
    const loadLinked = async () => {
      if (!selectedPO?.id) {
        setLinkedReceivers([]);
        setLinkedInvoices([]);
        return;
      }
      try {
        const [recRes, invRes] = await Promise.all([
          purchaseOrderService.getShipmentsInForPO(selectedPO.id),
          vendorInvoiceService.getAll({ purchase_order_id: selectedPO.id, limit: 100 })
        ]);
        setLinkedReceivers(recRes?.data ?? []);
        setLinkedInvoices(invRes?.data ?? []);
      } catch {
        setLinkedReceivers([]);
        setLinkedInvoices([]);
      }
    };
    loadLinked();
  }, [selectedPO?.id]);

  useEffect(() => {
    vendorService.getAll({ limit: 500, activeOnly: false }).then((res) => setVendors(res?.data ?? [])).catch(() => {});
  }, []);

  useEffect(() => {
    employeeService.getAll({ limit: 500, activeOnly: 'true' }).then((res) => setEmployees(res?.data ?? [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedPO?.id) {
      loadAttachments(selectedPO.id);
    } else {
      setAttachments([]);
    }
  }, [selectedPO?.id]);

  // Prevent browser opening files dropped outside drop-zone.
  useEffect(() => {
    const preventDefault = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('dragover', preventDefault);
    document.addEventListener('drop', preventDefault);
    return () => {
      document.removeEventListener('dragover', preventDefault);
      document.removeEventListener('drop', preventDefault);
    };
  }, []);

  const loadAttachments = async (poId) => {
    if (!poId) {
      setAttachments([]);
      return;
    }
    try {
      setAttachmentsLoading(true);
      setAttachmentsError('');
      const res = await attachmentService.getForPurchaseOrder(poId);
      const list = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
      setAttachments(list);
    } catch (err) {
      console.error('Error loading PO attachments:', err);
      setAttachmentsError(err.response?.data?.message || 'Failed to load attachments');
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const uploadAttachments = async (files) => {
    if (!selectedPO?.id || !files?.length) return;
    if (files.length > 5) {
      setAttachmentsError('You can upload a maximum of 5 files at a time.');
      return;
    }
    try {
      setAttachmentsError('');
      for (const file of files) {
        await attachmentService.upload({ file, purchaseOrderId: selectedPO.id });
      }
      await loadAttachments(selectedPO.id);
    } catch (err) {
      console.error('Error uploading PO attachment:', err);
      setAttachmentsError(err.response?.data?.message || 'Failed to upload attachment');
    }
  };

  const handleFileInputChange = async (event) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;
    await uploadAttachments(Array.from(fileList));
    event.target.value = '';
  };

  const handleAttachmentDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOverAttachment(false);
    const fileList = event.dataTransfer?.files;
    if (!fileList || fileList.length === 0) return;
    await uploadAttachments(Array.from(fileList));
  };

  const handleAttachmentDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    setIsDraggingOverAttachment(true);
  };

  const handleAttachmentDragLeave = (event) => {
    event.preventDefault();
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDraggingOverAttachment(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      setAttachmentsError('');
      await attachmentService.delete(attachmentId);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (err) {
      console.error('Error deleting attachment:', err);
      setAttachmentsError(err.response?.data?.message || 'Failed to delete attachment');
    }
  };

  const handleDownloadAttachment = async (att) => {
    try {
      setAttachmentsError('');
      const res = await attachmentService.download(att.id);
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.file_name || `attachment-${att.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading attachment:', err);
      setAttachmentsError(err.response?.data?.message || 'Failed to download attachment');
    }
  };

  const handleSearch = () => {
    loadPOs();
  };

  const openNewDialog = () => {
    setPoForm({
      ...emptyPOForm(),
      // Default ordered_by to the currently logged-in user
      ordered_by_id: user?.id ?? ''
    });
    setLineItems([emptyLineItem()]);
    setNewDialogOpen(true);
    setError('');
  };

  const handleCreatePO = async () => {
    const { vendor_id } = poForm;
    if (!vendor_id) {
      setError('Vendor is required. Add vendors under Procurement → Vendors.');
      return;
    }
    const items = lineItems
      .filter((it) => (it.part_number || it.description || '').trim() || Number(it.quantity_ordered) > 0)
      .map((it, idx) => ({
        line_number: idx + 1,
        part_number: it.part_number || null,
        part_id: it.part_id ?? null,
        description: it.description || null,
        quantity_ordered: Math.max(0, parseInt(it.quantity_ordered, 10) || 0),
        unit: it.unit || 'EA',
        unit_price: it.unit_price !== '' && it.unit_price != null ? Number(it.unit_price) : null,
        notes: it.notes || null,
        work_order_id: it.work_order_id ?? null,
        work_order_item_id: it.work_order_item_id ?? null
      }));
    if (items.length === 0) {
      setError('At least one line item is required (part number, description, or quantity).');
      return;
    }
    try {
      setSaving(true);
      setError('');
      await purchaseOrderService.create({
        vendor_id: Number(poForm.vendor_id),
        ordered_by_id: poForm.ordered_by_id ? Number(poForm.ordered_by_id) : null,
        order_date: poForm.order_date,
        expected_date: poForm.expected_date || null,
        ship_via: poForm.ship_via || null,
        notes: poForm.notes || null,
        items
      });
      setNewDialogOpen(false);
      loadPOs();
    } catch (err) {
      console.error('Error creating purchase order:', err);
      setError(err.response?.data?.message || 'Failed to create purchase order');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = async (row) => {
    if (!row) return;
    setSelectedPO(row);
    setEditDialogOpen(true);
    setError('');
    setLinkedReceivers([]);
    setLinkedInvoices([]);
    try {
      const [poRes, recRes, invRes] = await Promise.all([
        purchaseOrderService.getById(row.id),
        purchaseOrderService.getShipmentsInForPO(row.id),
        vendorInvoiceService.getAll({ purchase_order_id: row.id, limit: 100 })
      ]);
      const data = poRes?.data;
      if (data) {
        setPoForm({
          po_number: data.identifier ?? data.po_number ?? '',
          vendor_id: data.vendor_id ?? '',
          vendor_name: data.vendor_name ?? '',
          ordered_by_id: data.ordered_by_id ?? '',
          order_date: (data.order_date || '').toString().slice(0, 10) || formatLocalTodayYmd(),
          expected_date: (data.expected_date || '').toString().slice(0, 10) || '',
          ship_via: data.ship_via ?? '',
          notes: data.notes ?? ''
        });
        setLineItems(
          (data.items || []).length > 0
            ? data.items.map((it) => ({
                id: it.id,
                line_number: it.line_number ?? 0,
                part_number: it.part_number ?? '',
                description: it.description ?? it.part_description ?? '',
                quantity_ordered: parseInt(it.quantity_ordered, 10) || 0,
                unit: it.unit ?? 'EA',
                unit_price: it.unit_price ?? '',
                notes: it.notes ?? '',
                work_order_id: it.work_order_id ?? null,
                work_order_item_id: it.work_order_item_id ?? null
              }))
            : [emptyLineItem()]
        );
        setLinkedReceivers(recRes?.data ?? []);
        setLinkedInvoices(invRes?.data ?? []);
      } else {
        setPoForm(emptyPOForm());
        setLineItems([emptyLineItem()]);
      }
    } catch (err) {
      console.error('Error loading purchase order:', err);
      setError(err.response?.data?.message || 'Failed to load purchase order');
    }
  };

  const handleUpdatePO = async () => {
    if (!selectedPO?.id) return;
    const { vendor_id } = poForm;
    if (!vendor_id) {
      setError('Vendor is required.');
      return;
    }
    const items = lineItems
      .filter((it) => (it.part_number || it.description || '').trim() || Number(it.quantity_ordered) > 0)
      .map((it, idx) => ({
        line_number: idx + 1,
        part_number: it.part_number || null,
        part_id: it.part_id ?? null,
        description: it.description || null,
        quantity_ordered: Math.max(0, parseInt(it.quantity_ordered, 10) || 0),
        unit: it.unit || 'EA',
        unit_price: it.unit_price !== '' && it.unit_price != null ? Number(it.unit_price) : null,
        notes: it.notes || null,
        work_order_id: it.work_order_id ?? null,
        work_order_item_id: it.work_order_item_id ?? null
      }));
    if (items.length === 0) {
      setError('At least one line item is required (part number, description, or quantity).');
      return;
    }
    try {
      setSaving(true);
      setError('');
      await purchaseOrderService.update(selectedPO.id, {
        po_number: selectedPO.identifier ?? poForm.po_number,
        vendor_id: Number(poForm.vendor_id),
        ordered_by_id: poForm.ordered_by_id ? Number(poForm.ordered_by_id) : null,
        order_date: poForm.order_date,
        expected_date: poForm.expected_date || null,
        ship_via: poForm.ship_via || null,
        notes: poForm.notes || null,
        items
      });
      setEditDialogOpen(false);
      setSelectedPO(null);
      loadPOs();
    } catch (err) {
      console.error('Error updating purchase order:', err);
      setError(err.response?.data?.message || 'Failed to update purchase order');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePO = async () => {
    if (!selectedPO?.id) return;
    try {
      setSaving(true);
      setError('');
      await purchaseOrderService.delete(selectedPO.id);
      setDeleteConfirmOpen(false);
      setSelectedPO(null);
      loadPOs();
    } catch (err) {
      console.error('Error deleting purchase order:', err);
      setError(err.response?.data?.message || 'Failed to delete purchase order');
    } finally {
      setSaving(false);
    }
  };

  const addLineItem = () => {
    setLineItems((prev) => [...prev, { ...emptyLineItem(), line_number: prev.length + 1 }]);
  };

  const updateLineItem = (idx, field, value) => {
    setLineItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };

  const removeLineItem = (idx) => {
    setLineItems((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length === 0 ? [emptyLineItem()] : next.map((it, i) => ({ ...it, line_number: i + 1 }));
    });
  };

  const formatDate = (v) => formatDateOnly(v, 'MM/dd/yyyy') || '—';

  const handleCancelPO = async () => {
    if (!selectedPO?.id) return;
    try {
      setSaving(true);
      setError('');
      await purchaseOrderService.update(selectedPO.id, { status: 'CANCELLED' });
      setCancelConfirmOpen(false);
      await loadPOs();
    } catch (err) {
      console.error('Error cancelling purchase order:', err);
      setError(err.response?.data?.message || 'Failed to cancel purchase order');
    } finally {
      setSaving(false);
    }
  };

  const handlePrintPo = (row) => {
    if (!row) return;
    navigate(`/procurement/po/${row.id}?print=1`);
  };

  const handlePoReceiverAction = (row) => {
    if (!row) return;
    if (row.has_receiver) {
      navigate(`/procurement/shipments-in?po=${encodeURIComponent(row.po_number || '')}`);
      return;
    }
    navigate(`/procurement/shipments-in/new?po=${encodeURIComponent(row.po_number || '')}`);
  };

  const handlePrintInvoice = (row) => {
    if (!row?.first_invoice_id) return;
    navigate(`/procurement/invoices?invoice=${row.first_invoice_id}&print=1`);
  };

  return (
    <>
      {/* Print-friendly purchase order list (PrintPage letterhead) */}
      <PrintPage
        title="Purchase Orders"
        footer={
          <div className="print-footer">
            Generated on {format(new Date(), 'MM/dd/yyyy hh:mm a')}
          </div>
        }
      >
        <table className="print-table" style={{ width: '100%', marginBottom: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>P.O. number</th>
              <th style={{ textAlign: 'left' }}>Vendor</th>
              <th style={{ textAlign: 'left' }}>Ordered by</th>
              <th style={{ textAlign: 'left' }}>Order date</th>
              <th style={{ textAlign: 'left' }}>Expected</th>
              <th style={{ textAlign: 'left' }}>Ship via</th>
              <th style={{ textAlign: 'left' }}>Receipt status</th>
            </tr>
          </thead>
          <tbody>
            {pos.length > 0 ? (
              pos.map((row) => (
                <tr key={row.id}>
                  <td>{row.identifier ?? row.po_number}</td>
                  <td>{row.vendor_name}</td>
                  <td>
                    {row.ordered_by_first_name || row.ordered_by_last_name
                      ? `${row.ordered_by_first_name || ''} ${row.ordered_by_last_name || ''}`.trim()
                      : '—'}
                  </td>
                  <td>{formatDateOnly(row.order_date, 'MM/dd/yyyy') || '—'}</td>
                  <td>{formatDateOnly(row.expected_date, 'MM/dd/yyyy') || '—'}</td>
                  <td>{row.ship_via || '—'}</td>
                  <td>{getPoReceiptStatusChipProps(row.status).label}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center' }}>No purchase orders</td>
              </tr>
            )}
          </tbody>
        </table>
      </PrintPage>
      <Box className="no-print-procurement-po">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          {isDetailMode ? `Purchase Order ${selectedPO?.identifier ?? selectedPO?.po_number ?? ''}`.trim() : 'P.O. (Purchase Orders)'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isDetailMode && (
            <Button variant="outlined" size="small" startIcon={<ArrowBack />} onClick={() => navigate('/procurement/po')}>
              Back to list
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => selectedPO && navigate(`/procurement/po/${selectedPO.id}`)}
            disabled={!selectedPO}
          >
            Edit
          </Button>
          <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => selectedPO && setDeleteConfirmOpen(true)} disabled={!selectedPO}>
            Delete
          </Button>
          {selectedPO?.status !== 'CANCELLED' && (
            <Button variant="outlined" color="warning" size="small" onClick={() => setCancelConfirmOpen(true)} disabled={!selectedPO}>
              Cancel P.O.
            </Button>
          )}
          {!isDetailMode && (
            <Button variant="outlined" size="small" startIcon={<Print />} onClick={() => printWithFilename('Purchase-Orders')}>
              Print list
            </Button>
          )}
          <Button variant="contained" color="success" size="small" startIcon={<AddIcon />} onClick={() => navigate('/procurement/po/new')}>
            New
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Selected PO summary (detail view) */}
      {isDetailMode && selectedPO && (
        <ProcurementSummaryBar>
          <ProcurementSummaryField label="P.O. #">
            <Box sx={{ fontWeight: 600 }}>{selectedPO.identifier ?? selectedPO.po_number ?? '—'}</Box>
          </ProcurementSummaryField>
          <ProcurementSummaryField label="Vendor">{selectedPO.vendor_name ?? '—'}</ProcurementSummaryField>
          <ProcurementSummaryField label="Ordered By">
            {selectedPO.ordered_by_first_name || selectedPO.ordered_by_last_name
              ? `${selectedPO.ordered_by_first_name || ''} ${selectedPO.ordered_by_last_name || ''}`.trim()
              : '—'}
          </ProcurementSummaryField>
          <ProcurementSummaryField label="Order Date">{formatDate(selectedPO.order_date)}</ProcurementSummaryField>
          <ProcurementSummaryField label="Expected Date">{formatDate(selectedPO.expected_date)}</ProcurementSummaryField>
          <ProcurementSummaryField label="Ship Via">{selectedPO.ship_via ?? '—'}</ProcurementSummaryField>
          <ProcurementSummaryField label="Receipt status">
            <PoReceiptStatusChip status={selectedPO.status} />
          </ProcurementSummaryField>
          <ProcurementSummaryField label="Shipments received" wide>
            <ShipmentsReceivedSummaryLinks receivers={linkedReceivers} />
          </ProcurementSummaryField>
          <ProcurementSummaryField label="Notes" wide>
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedPO.notes ?? '—'}</Box>
          </ProcurementSummaryField>
        </ProcurementSummaryBar>
      )}

      {/* List view: summary for selected row */}
      {!isDetailMode && selectedPO && (
        <ProcurementSummaryBar>
          <ProcurementSummaryField label="P.O. #">
            <Box sx={{ fontWeight: 600 }}>{selectedPO.identifier ?? selectedPO.po_number ?? '—'}</Box>
          </ProcurementSummaryField>
          <ProcurementSummaryField label="Vendor">{selectedPO.vendor_name ?? '—'}</ProcurementSummaryField>
          <ProcurementSummaryField label="Order Date">{formatDate(selectedPO.order_date)}</ProcurementSummaryField>
          <ProcurementSummaryField label="Receipt status">
            <PoReceiptStatusChip status={selectedPO.status} />
          </ProcurementSummaryField>
          <ProcurementSummaryField label="Ship Via">{selectedPO.ship_via ?? '—'}</ProcurementSummaryField>
          <ProcurementSummaryField label="Shipments received" wide>
            <ShipmentsReceivedSummaryLinks receivers={linkedReceivers} />
          </ProcurementSummaryField>
        </ProcurementSummaryBar>
      )}

      {isDetailMode && selectedPO && (
      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Box
          onDrop={handleAttachmentDrop}
          onDragOver={handleAttachmentDragOver}
          onDragLeave={handleAttachmentDragLeave}
          sx={{
            p: 2,
            mb: 1,
            minHeight: 120,
            border: '2px dashed',
            borderColor: isDraggingOverAttachment ? 'primary.main' : 'grey.400',
            borderRadius: 2,
            bgcolor: isDraggingOverAttachment ? 'action.hover' : 'grey.50',
            color: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            transition: 'border-color 0.15s ease, background-color 0.15s ease'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CloudUpload sx={{ fontSize: 40, opacity: isDraggingOverAttachment ? 1 : 0.6 }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Drop documents for PO {selectedPO?.identifier ?? selectedPO?.po_number}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                or use the Upload button · max 5 files, 10 MB each
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Attachments: {attachments.length}{selectedPO?.identifier ? ` for ${selectedPO.identifier}` : ''}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            component="label"
            size="small"
            startIcon={<CloudUpload />}
            disabled={!selectedPO}
          >
            Upload
            <input type="file" hidden multiple onChange={handleFileInputChange} />
          </Button>
        </Box>
        {attachmentsError && <Alert severity="error" sx={{ mb: 1 }}>{attachmentsError}</Alert>}
        {attachmentsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={24} />
          </Box>
        ) : attachments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No attachments yet. Drag and drop files onto the area above or use the Upload button.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><Typography variant="caption">File Name</Typography></TableCell>
                  <TableCell><Typography variant="caption">Size</Typography></TableCell>
                  <TableCell><Typography variant="caption">Uploaded At</Typography></TableCell>
                  <TableCell width={80}><Typography variant="caption">Actions</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attachments.map((att) => (
                  <TableRow key={att.id} hover>
                    <TableCell>
                      <Button
                        size="small"
                        sx={{ textTransform: 'none' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadAttachment(att);
                        }}
                      >
                        {att.file_name}
                      </Button>
                    </TableCell>
                    <TableCell>{att.file_size != null ? `${(att.file_size / 1024 / 1024).toFixed(2)} MB` : '—'}</TableCell>
                    <TableCell>{att.uploaded_at ? format(new Date(att.uploaded_at), 'MM/dd/yyyy HH:mm') : '—'}</TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() => handleDeleteAttachment(att.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      )}

      {isDetailMode && selectedPO && (
      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Flow Links (PO → Shipment Received → Vendor Invoice)</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" startIcon={<TextSnippet />} variant="outlined" onClick={() => navigate(`/procurement/shipments-in/new?po=${encodeURIComponent(selectedPO.po_number || '')}`)}>
            Create received shipment
          </Button>
          <Button size="small" startIcon={<Visibility />} variant="outlined" onClick={() => navigate(`/procurement/shipments-in?po=${encodeURIComponent(selectedPO.po_number || '')}`)} disabled={linkedReceivers.length === 0}>
            View shipments ({linkedReceivers.length})
          </Button>
          <Button size="small" startIcon={<ReceiptLong />} variant="outlined" onClick={() => navigate(`/procurement/invoices?po=${selectedPO.id}&openNew=1`)}>
            Create vendor invoice
          </Button>
          <Button size="small" startIcon={<Visibility />} variant="outlined" onClick={() => navigate(`/procurement/invoices?po=${selectedPO.id}`)} disabled={linkedInvoices.length === 0}>
            View invoices ({linkedInvoices.length})
          </Button>
        </Box>
      </Paper>
      )}

      {!isDetailMode && (
      <Paper sx={{ p: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by P.O. number or vendor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" edge="end" onClick={handleSearch}>
                    <Search />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 220 }}
          />
          <Button variant="outlined" size="small" onClick={handleSearch}>
            Search
          </Button>
          <IconButton
            size="small"
            onClick={() => setColumnsDialogOpen(true)}
            title="Adjust column widths"
            sx={{ ml: 0.5 }}
          >
            <ViewColumn fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
      )}

      {!isDetailMode && (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <ResizableHeaderCell width={colWidths.po} onResizeStart={(e) => startResize('po', e)} sortable sortActive={sortKey === 'po_number'} sortDirection={sortDirection} onSort={() => handleSort('po_number')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>P.O. Number</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.vendor} onResizeStart={(e) => startResize('vendor', e)} sortable sortActive={sortKey === 'vendor'} sortDirection={sortDirection} onSort={() => handleSort('vendor')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Vendor</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.orderedBy} onResizeStart={(e) => startResize('orderedBy', e)} sortable sortActive={sortKey === 'ordered_by'} sortDirection={sortDirection} onSort={() => handleSort('ordered_by')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Ordered By</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.orderDate} onResizeStart={(e) => startResize('orderDate', e)} sortable sortActive={sortKey === 'order_date'} sortDirection={sortDirection} onSort={() => handleSort('order_date')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Order Date</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.expected} onResizeStart={(e) => startResize('expected', e)} sortable sortActive={sortKey === 'expected_date'} sortDirection={sortDirection} onSort={() => handleSort('expected_date')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Expected</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.shipmentsReceived} onResizeStart={(e) => startResize('shipmentsReceived', e)} sortable sortActive={sortKey === 'shipments_in_count'} sortDirection={sortDirection} onSort={() => handleSort('shipments_in_count')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Shipments received</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.shipVia} onResizeStart={(e) => startResize('shipVia', e)} sortable sortActive={sortKey === 'ship_via'} sortDirection={sortDirection} onSort={() => handleSort('ship_via')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Ship Via</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.status} onResizeStart={(e) => startResize('status', e)} sortable sortActive={sortKey === 'status'} sortDirection={sortDirection} onSort={() => handleSort('status')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Status</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.actions} onResizeStart={(e) => startResize('actions', e)}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Actions</Typography></ResizableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : pos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No purchase orders. Use New to create one, then use Shipments Received to create a receiver from a PO #.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayedPos.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  selected={selectedPO?.id === row.id}
                  onClick={() => setSelectedPO(row)}
                  onDoubleClick={() => navigate(`/procurement/po/${row.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ width: colWidths.po }}>{row.identifier ?? row.po_number ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.vendor }}>{row.vendor_name ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.orderedBy }}>
                    {row.ordered_by_first_name || row.ordered_by_last_name
                      ? `${row.ordered_by_first_name || ''} ${row.ordered_by_last_name || ''}`.trim()
                      : '—'}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.orderDate }}>{formatDate(row.order_date)}</TableCell>
                  <TableCell sx={{ width: colWidths.expected }}>{formatDate(row.expected_date)}</TableCell>
                  <TableCell sx={{ width: colWidths.shipmentsReceived }} onClick={(e) => e.stopPropagation()}>
                    {getPoShipmentCount(row) <= 0 ? (
                      '—'
                    ) : (
                      <Link
                        component={RouterLink}
                        to={`/procurement/shipments-in?po=${encodeURIComponent(row.po_number || '')}`}
                        variant="body2"
                        underline="hover"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {getPoShipmentCount(row)}
                      </Link>
                    )}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.shipVia }}>{row.ship_via ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.status }}>
                    <PoReceiptStatusChip status={row.status} />
                  </TableCell>
                  <TableCell sx={{ width: colWidths.actions }} onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <IconButton size="small" color="primary" title="Print purchase order" onClick={() => handlePrintPo(row)}>
                        <Print fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="success" title="Open PO detail" onClick={() => navigate(`/procurement/po/${row.id}`)}>
                        <Visibility fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color={row.has_receiver ? 'success' : 'primary'}
                        title={row.has_receiver ? 'View shipments received' : 'Create received shipment'}
                        onClick={() => handlePoReceiverAction(row)}
                      >
                        {row.has_receiver ? <Visibility fontSize="small" /> : <TextSnippet fontSize="small" />}
                      </IconButton>
                      <IconButton
                        size="small"
                        color={row.has_invoice ? 'primary' : 'inherit'}
                        title={row.has_invoice ? `Print invoice ${row.first_invoice_identifier || ''}`.trim() : 'No invoice linked'}
                        onClick={() => handlePrintInvoice(row)}
                        disabled={!row.has_invoice}
                      >
                        <ReceiptLong fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* New PO dialog */}
      <Dialog open={columnsDialogOpen} onClose={() => setColumnsDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Adjust table columns</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 1.25, mt: 0.5 }}>
            <TextField size="small" type="number" label="P.O. Number width" value={colWidths.po} onChange={(e) => setColWidths((p) => ({ ...p, po: Number(e.target.value) || 0 }))} />
            <TextField size="small" type="number" label="Vendor width" value={colWidths.vendor} onChange={(e) => setColWidths((p) => ({ ...p, vendor: Number(e.target.value) || 0 }))} />
            <TextField size="small" type="number" label="Ordered By width" value={colWidths.orderedBy} onChange={(e) => setColWidths((p) => ({ ...p, orderedBy: Number(e.target.value) || 0 }))} />
            <TextField size="small" type="number" label="Order Date width" value={colWidths.orderDate} onChange={(e) => setColWidths((p) => ({ ...p, orderDate: Number(e.target.value) || 0 }))} />
            <TextField size="small" type="number" label="Expected width" value={colWidths.expected} onChange={(e) => setColWidths((p) => ({ ...p, expected: Number(e.target.value) || 0 }))} />
            <TextField size="small" type="number" label="Shipments received width" value={colWidths.shipmentsReceived} onChange={(e) => setColWidths((p) => ({ ...p, shipmentsReceived: Number(e.target.value) || 0 }))} />
            <TextField size="small" type="number" label="Ship Via width" value={colWidths.shipVia} onChange={(e) => setColWidths((p) => ({ ...p, shipVia: Number(e.target.value) || 0 }))} />
            <TextField size="small" type="number" label="Status width" value={colWidths.status} onChange={(e) => setColWidths((p) => ({ ...p, status: Number(e.target.value) || 0 }))} />
            <TextField size="small" type="number" label="Actions width" value={colWidths.actions} onChange={(e) => setColWidths((p) => ({ ...p, actions: Number(e.target.value) || 0 }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetColWidths}>Reset</Button>
          <Button onClick={() => setColumnsDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              saveColWidths(colWidths);
              setColumnsDialogOpen(false);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={newDialogOpen} onClose={() => !saving && setNewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Purchase Order</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="P.O. #"
                value={newPoIdentifierPreview}
                InputProps={{ readOnly: true }}
                helperText="Assigned on save (preview updates when order date changes)."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={vendors}
                getOptionLabel={(opt) => opt?.name ?? ''}
                value={vendors.find((v) => v.id === poForm.vendor_id) ?? null}
                onChange={(_, val) => setPoForm((f) => ({ ...f, vendor_id: val?.id ?? '', vendor_name: val?.name ?? '' }))}
                renderInput={(params) => <TextField {...params} size="small" label="Vendor" required />}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={employees}
                getOptionLabel={(opt) => opt ? `${opt.first_name || ''} ${opt.last_name || ''}`.trim() || opt.email || '—' : ''}
                value={employees.find((e) => e.id === poForm.ordered_by_id) ?? null}
                onChange={(_, val) => setPoForm((f) => ({ ...f, ordered_by_id: val?.id ?? '' }))}
                renderInput={(params) => <TextField {...params} size="small" label="Ordered By" />}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Order date" type="date" value={poForm.order_date} onChange={(e) => setPoForm((f) => ({ ...f, order_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Expected date" type="date" value={poForm.expected_date} onChange={(e) => setPoForm((f) => ({ ...f, expected_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Ship via</InputLabel>
                <Select
                  label="Ship via"
                  value={poForm.ship_via || ''}
                  onChange={(e) => setPoForm((f) => ({ ...f, ship_via: e.target.value }))}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {SHIP_VIA_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" multiline rows={2} value={poForm.notes} onChange={(e) => setPoForm((f) => ({ ...f, notes: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Line items</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><Typography variant="caption">Part #</Typography></TableCell>
                      <TableCell><Typography variant="caption">Description</Typography></TableCell>
                      <TableCell align="right"><Typography variant="caption">Qty</Typography></TableCell>
                      <TableCell><Typography variant="caption">Unit</Typography></TableCell>
                      <TableCell align="right"><Typography variant="caption">Unit price</Typography></TableCell>
                      <TableCell align="right"><Typography variant="caption">Line total</Typography></TableCell>
                      <TableCell width={40} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItems.map((it, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <TextField size="small" value={it.part_number} onChange={(e) => updateLineItem(idx, 'part_number', e.target.value)} placeholder="Part #" sx={{ width: 100 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" fullWidth value={it.description} onChange={(e) => updateLineItem(idx, 'description', e.target.value)} placeholder="Description" />
                        </TableCell>
                        <TableCell align="right">
                          <TextField type="number" size="small" inputProps={{ min: 0, step: 1 }} value={it.quantity_ordered ?? ''} onChange={(e) => { const v = parseInt(e.target.value, 10); updateLineItem(idx, 'quantity_ordered', isNaN(v) ? '' : Math.max(0, v)); }} sx={{ width: 80 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" value={it.unit} onChange={(e) => updateLineItem(idx, 'unit', e.target.value)} sx={{ width: 56 }} />
                        </TableCell>
                        <TableCell align="right">
                          <TextField type="number" size="small" inputProps={{ min: 0, step: 0.01 }} value={it.unit_price ?? ''} onChange={(e) => updateLineItem(idx, 'unit_price', e.target.value)} sx={{ width: 90 }} />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ pr: 0.5, whiteSpace: 'nowrap' }}>
                            {formatCurrency(computePOLineExtended(it))}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => removeLineItem(idx)} disabled={lineItems.length <= 1}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={5} align="right" sx={{ borderBottom: 'none', fontWeight: 600 }}>
                        <Typography variant="subtitle2">Total</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: 'none', fontWeight: 600 }}>
                        <Typography variant="subtitle2">{formatCurrency(computePOLinesSubtotal(lineItems))}</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none' }} />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
              <Button size="small" startIcon={<AddIcon />} onClick={addLineItem} sx={{ mt: 0.5 }}>
                Add line
              </Button>
              {validLineItems(lineItems).length === 0 && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  At least one line item is required (part #, description, or quantity).
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleCreatePO} disabled={saving || validLineItems(lineItems).length === 0}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit PO dialog */}
      <Dialog open={editDialogOpen} onClose={() => !saving && (setEditDialogOpen(false), setSelectedPO(null))} maxWidth="md" fullWidth>
        <DialogTitle>Edit Purchase Order</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="P.O. #"
                value={selectedPO?.identifier ?? poForm.po_number ?? ''}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={vendors}
                getOptionLabel={(opt) => opt?.name ?? ''}
                value={vendors.find((v) => v.id === poForm.vendor_id) ?? null}
                onChange={(_, val) => setPoForm((f) => ({ ...f, vendor_id: val?.id ?? '', vendor_name: val?.name ?? '' }))}
                renderInput={(params) => <TextField {...params} size="small" label="Vendor" required />}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={employees}
                getOptionLabel={(opt) => opt ? `${opt.first_name || ''} ${opt.last_name || ''}`.trim() || opt.email || '—' : ''}
                value={employees.find((e) => e.id === poForm.ordered_by_id) ?? null}
                onChange={(_, val) => setPoForm((f) => ({ ...f, ordered_by_id: val?.id ?? '' }))}
                renderInput={(params) => <TextField {...params} size="small" label="Ordered By" />}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Order date" type="date" value={poForm.order_date} onChange={(e) => setPoForm((f) => ({ ...f, order_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Expected date" type="date" value={poForm.expected_date} onChange={(e) => setPoForm((f) => ({ ...f, expected_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Ship via" value={poForm.ship_via} onChange={(e) => setPoForm((f) => ({ ...f, ship_via: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" multiline rows={2} value={poForm.notes} onChange={(e) => setPoForm((f) => ({ ...f, notes: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Line items</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><Typography variant="caption">Part #</Typography></TableCell>
                      <TableCell><Typography variant="caption">Description</Typography></TableCell>
                      <TableCell align="right"><Typography variant="caption">Qty</Typography></TableCell>
                      <TableCell><Typography variant="caption">Unit</Typography></TableCell>
                      <TableCell align="right"><Typography variant="caption">Unit price</Typography></TableCell>
                      <TableCell align="right"><Typography variant="caption">Line total</Typography></TableCell>
                      <TableCell width={40} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItems.map((it, idx) => (
                      <TableRow key={it.id ?? idx}>
                        <TableCell>
                          <TextField size="small" value={it.part_number} onChange={(e) => updateLineItem(idx, 'part_number', e.target.value)} placeholder="Part #" sx={{ width: 100 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" fullWidth value={it.description} onChange={(e) => updateLineItem(idx, 'description', e.target.value)} placeholder="Description" />
                        </TableCell>
                        <TableCell align="right">
                          <TextField type="number" size="small" inputProps={{ min: 0, step: 1 }} value={it.quantity_ordered ?? ''} onChange={(e) => { const v = parseInt(e.target.value, 10); updateLineItem(idx, 'quantity_ordered', isNaN(v) ? '' : Math.max(0, v)); }} sx={{ width: 80 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" value={it.unit} onChange={(e) => updateLineItem(idx, 'unit', e.target.value)} sx={{ width: 56 }} />
                        </TableCell>
                        <TableCell align="right">
                          <TextField type="number" size="small" inputProps={{ min: 0, step: 0.01 }} value={it.unit_price ?? ''} onChange={(e) => updateLineItem(idx, 'unit_price', e.target.value)} sx={{ width: 90 }} />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ pr: 0.5, whiteSpace: 'nowrap' }}>
                            {formatCurrency(computePOLineExtended(it))}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => removeLineItem(idx)} disabled={lineItems.length <= 1}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={5} align="right" sx={{ borderBottom: 'none', fontWeight: 600 }}>
                        <Typography variant="subtitle2">Total</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: 'none', fontWeight: 600 }}>
                        <Typography variant="subtitle2">{formatCurrency(computePOLinesSubtotal(lineItems))}</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none' }} />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
              <Button size="small" startIcon={<AddIcon />} onClick={addLineItem} sx={{ mt: 0.5 }}>
                Add line
              </Button>
              {validLineItems(lineItems).length === 0 && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  At least one line item is required (part #, description, or quantity).
                </Typography>
              )}
            </Grid>
            {linkedReceivers.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Linked receivers (traceability)</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {linkedReceivers.map((r) => (
                    <Button
                      key={r.id}
                      size="small"
                      variant="outlined"
                      startIcon={<LinkIcon />}
                      onClick={() => { setEditDialogOpen(false); setSelectedPO(null); navigate(`/procurement/shipments-in/${r.id}`); }}
                      >
                      {r.identifier ?? r.receiver_no}
                    </Button>
                  ))}
                </Box>
              </Grid>
            )}
            <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Linked invoices</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                  {linkedInvoices.length > 0 ? (
                    linkedInvoices.map((inv) => (
                      <Button
                        key={inv.id}
                        size="small"
                        variant="outlined"
                        startIcon={<LinkIcon />}
                        onClick={() => { setEditDialogOpen(false); setSelectedPO(null); navigate(`/procurement/invoices?po=${selectedPO?.id}`); }}
                        >
                        {inv.identifier ?? inv.invoice_number} {inv.invoice_date ? `(${formatDateOnly(inv.invoice_date, 'MM/dd/yy')})` : ''}
                      </Button>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>No linked invoices</Typography>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => { setEditDialogOpen(false); setSelectedPO(null); navigate(`/procurement/invoices?po=${selectedPO?.id}&openNew=1`); }}
                  >
                    Add invoice
                  </Button>
                </Box>
              </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); setSelectedPO(null); }} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdatePO} disabled={saving || validLineItems(lineItems).length === 0}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelConfirmOpen} onClose={() => !saving && setCancelConfirmOpen(false)}>
        <DialogTitle>Cancel Purchase Order</DialogTitle>
        <DialogContent>
          <Typography>
            Mark P.O. <strong>{selectedPO?.identifier ?? selectedPO?.po_number ?? selectedPO?.id}</strong> as <strong>CANCELLED</strong>? Receipt status will no longer update from shipments.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirmOpen(false)} disabled={saving}>
            Back
          </Button>
          <Button variant="contained" color="warning" onClick={handleCancelPO} disabled={saving}>
            {saving ? 'Saving…' : 'Cancel P.O.'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteConfirmOpen} onClose={() => !saving && setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Purchase Order</DialogTitle>
        <DialogContent>
          <Typography>
            Delete P.O. <strong>{selectedPO?.identifier ?? selectedPO?.po_number ?? selectedPO?.id}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeletePO} disabled={saving}>
            {saving ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </>
  );
};

export default ProcurementPO;
